// src/config/featureEntitlements.ts
//
// Resolves the effective availability of per-tenant features by combining the
// entitlements the backend reports on GET /users/me with the legacy build-time
// feature flags.
//
// The add-in ships as a single shared multi-tenant build, so a build-time flag
// can't express a per-tenant decision. When the backend reports an entitlement
// it is authoritative (it can enable or disable the feature); when it reports
// nothing — an older backend, or the bootstrap call failed — we fall back to the
// build flag so behavior is unchanged until the backend starts sending the value.
//
// To add a per-tenant feature: add the field to `FeatureEntitlements` (the wire
// type, in userMeApi) and to `EffectiveEntitlements` below, then add one line to
// `resolveEntitlements` mapping it to its build-flag fallback.

import { FEATURE_FLAGS } from "./featureFlags";
import type { FeatureEntitlements } from "../api/userMeApi";

/** Effective, ready-to-consume availability of each per-tenant feature. */
export interface EffectiveEntitlements {
  /** Document Library (SharePoint) menu item + modal. */
  documentLibrary: boolean;
  /** Restoring each agent's conversation after the add-in is reopened. */
  sessionPersistence: boolean;
  /** Tier 3 review: shows the Quick Analysis action on the empty chat screen. */
  tier3Review: boolean;
  /** Whether the concise writing style is offered as a user-facing toggle. */
  conciseMode: boolean;
  /**
   * Where the extended-thinking toggle starts on each app open.
   *
   * Not a switch for the feature - the control stays, and the user can turn
   * thinking on whenever they want. False only means it does not carry over
   * into their next session, so an expensive default has to be chosen rather
   * than inherited.
   */
  extendedThinkingDefaultOn: boolean;
}

/**
 * A backend-reported entitlement is authoritative when present; otherwise fall
 * back to the build-time flag.
 */
function resolve(entitlement: boolean | undefined, buildFlagFallback: boolean): boolean {
  return entitlement ?? buildFlagFallback;
}

/**
 * Combine the backend-reported entitlements (possibly null or partial) with the
 * build flags into the effective availability the UI consumes.
 */
export function resolveEntitlements(
  entitlements: FeatureEntitlements | null | undefined
): EffectiveEntitlements {
  return {
    documentLibrary: resolve(entitlements?.document_library, FEATURE_FLAGS.ENABLE_DOCUMENT_LIBRARY),
    // No build flag exists for this one: it is entitlement-only. The literal
    // fallback keeps it working against a backend old enough not to report it.
    sessionPersistence: resolve(entitlements?.session_persistence, true),
    // These two fall back to literals rather than build flags. Neither has ever
    // been build-configurable, and adding flags for them would mean threading
    // values through DefinePlugin and the Dockerfile that no environment would
    // ever set. The literals are each the "unchanged behavior" answer for a
    // backend that reports nothing: no Quick Analysis action, and the concise
    // toggle left exactly where every tenant has always had it.
    tier3Review: resolve(entitlements?.tier3_review, false),
    conciseMode: resolve(entitlements?.concise_mode, true),
    extendedThinkingDefaultOn: resolve(entitlements?.extended_thinking_default_on, true),
  };
}
